import { Component, Input } from '@angular/core';

import { TwcPopupService } from '@topcon/twc-angular';

import {
    TranslationService,
    EntryPointService,
    JobsService,
    ToastService,
    MagnetService,
    TopbarStateService,
} from '@mirage-projects/cw-commons';
import {
    CatalogObjectTypesEnum,
    JOB_STATUS_ERROR,
    Job,
    JobConfiguration,
    JobStatus,
    PcvizCollageWebFileBrowserListResult,
    PcvizExportProjectJobAdapter,
    PcvizJob,
    PcvizProject,
} from '@mirage-projects/cw-models';
import { JobPcvizService } from '@mirage-projects/cw-pcviz-core';
import { FileBrowserService } from '@mirage-projects/cw-utils';

export interface JobOnFinishObject {
    job: PcvizJob;
    configuration: JobConfiguration;
    exportProjectJobAdapter: PcvizExportProjectJobAdapter;
    project: PcvizProject;
}

/**
 * This component shows the popup to download the project
 */
@Component({
    selector: 'cw-popup-download-project',
    templateUrl: 'popup-download-project.component.html',
    styleUrls: ['./popup-download-project.component.scss'],
    standalone: false,
})
export class PopupDownloadProjectComponent {
    /** Project to download */
    @Input() project!: PcvizProject;

    isExported = false;
    jobOnProcess: Job | null = null;
    isDownloaded = false;
    isLoading = false;
    isPrincipalPopup = true;
    file: PcvizCollageWebFileBrowserListResult | undefined;
    cancelDialogName = '';

    constructor(
        private popupService: TwcPopupService,
        private jobAdapterService: JobPcvizService,
        private translationService: TranslationService,
        private entryPointService: EntryPointService,
        private fileBrowserService: FileBrowserService,
        private jobsService: JobsService,
        private toastService: ToastService,
        private magnetService: MagnetService,
        private topbarStateService: TopbarStateService
    ) {}
    /**
     * cancel the job download
     */
    async cancelJob(): Promise<void> {
        if (!this.jobOnProcess) {
            return;
        }
        await this.jobsService.cancel(this.jobOnProcess);
    }

    /*
     * close the popup if is not downloading
     */
    closePopup(): void {
        this.cancelJob();
        this.popupService.hideAll();
    }

    /*
     * activate the secondary popup if the job is throwed or close the popup if not
     */
    changePopups(): void {
        if (this.jobOnProcess) {
            this.isPrincipalPopup = false;
            return;
        }
        this.closePopup();
    }

    /**
     * download the file by manual button
     */
    manualDownload(): void {
        if (!this.file) {
            return;
        }
        this.fileBrowserService.download(this.file);
        this.closePopup();
    }
    /**
     * create the download job and download the file from project
     */
    async downloadProject(): Promise<void> {
        this.topbarStateService.disableAll();

        const project: PcvizProject = this.project;
        if (!project) {
            return;
        }

        const config: JobConfiguration = {
            jobName: `${project?.getName()} - ${this.translationService.translate('TAB_LAYERS.EXPORT_PROJECT').toLowerCase()}`,
            catalogId: this.entryPointService.getEntryPoint(CatalogObjectTypesEnum.projects, project?.getId().toString()),
            projectUuid: project?.getUuid(),
        };

        if (config.jobName) {
            this.cancelDialogName = config.jobName;
        }
        this.isExported = true;
        this.isLoading = true;

        const exportProjectJobAdapter = this.jobAdapterService.createExportProjectJobAdapter();
        exportProjectJobAdapter.setObjectName(project?.getName());
        exportProjectJobAdapter.setProjectType(project?.getType());

        const job = exportProjectJobAdapter.getCompleteJob(config);

        try {
            const process = await job.create(true);

            if (!process) {
                this.isExported = false;
                this.toastService.error('JOBS.ERROR_CREATE');
                return;
            }

            this.jobOnProcess = process;

            const jobOnFinishObject = {
                configuration: config,
                exportProjectJobAdapter,
                job,
                project,
            };

            this.jobOnFinish(jobOnFinishObject);
        } catch {
            this.isExported = false;
            this.toastService.error('JOBS.ERROR_CREATE');
        }
    }

    /**
     * make all the logic when job is finish
     * @param jobOnFinishObject
     */
    jobOnFinish(jobOnFinishObject: JobOnFinishObject): void {
        const jobStatusCancelled: JobStatus = 'CANCELLED';

        jobOnFinishObject.job.on('finish', async (jobData: Job) => {
            this.jobOnProcess = null;

            if (JOB_STATUS_ERROR.includes(jobData.status)) {
                this.toastService.generic('TAB_LAYERS.EXPORT_PROJECT_ERROR');
                return;
            }

            if (jobStatusCancelled === jobData.status) {
                return;
            }

            try {
                const file = await this.fileBrowserService.downloadFileFromProject(
                    jobOnFinishObject.exportProjectJobAdapter.getExportFileName(jobOnFinishObject.configuration),
                    jobOnFinishObject.project
                );
                this.isDownloaded = true;
                this.isLoading = false;
                this.file = file;
            } catch {
                this.toastService.error('FILE_BROWSER.ERROR_DOWNLOAD');
            } finally {
                this.topbarStateService.enableAll();
            }
        });
    }

    get isSite(): boolean {
        return this.magnetService.isSite();
    }
}
